import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CalendarClock, PiggyBank, TrendingUp } from "lucide-react";
import type {
  BillingCycle,
  Subscription,
} from "../types/subscription";
import { daysUntil, formatMoney, monthlyEquivalent } from "../lib/billing";
import { cn } from "../lib/cn";
import { buildToDefaultMultipliers } from "../lib/currency-convert";
import { useUserSettings } from "../lib/settings-context";
import { normalizeDefaultCurrency } from "../lib/user-settings";

const CHART_COLORS = [
  "oklch(0.78 0.14 195)",
  "oklch(0.72 0.16 280)",
  "oklch(0.8 0.12 145)",
  "oklch(0.75 0.18 55)",
  "oklch(0.7 0.12 25)",
  "oklch(0.65 0.08 260)",
];

type RenewalWindow = 30 | 60 | 90;

type SubscriptionChartRow = {
  id: number;
  name: string;
  short: string;
  value: number;
  currency: string;
  billing_cycle: BillingCycle;
  next_renewal_date: string;
  origMonthly?: number;
  origCurrency?: string;
};

type Props = {
  subscriptions: Subscription[];
};

type FxChartStatus = "loading" | "ready" | "error";

export function Dashboard({ subscriptions }: Props) {
  const { settings } = useUserSettings();
  const chartCurrency = normalizeDefaultCurrency(settings.defaultCurrency);
  const nfLocale = settings.numberLocale.trim() || undefined;
  const [renewalWindow, setRenewalWindow] = useState<RenewalWindow>(
    () => settings.dashboardDefaultRenewalWindow,
  );
  const [fxChartStatus, setFxChartStatus] = useState<FxChartStatus>("loading");
  const [fxToDefault, setFxToDefault] = useState<Map<string, number>>(
    () => new Map([[chartCurrency, 1]]),
  );

  const active = useMemo(
    () => subscriptions.filter((s) => s.active === 1),
    [subscriptions],
  );

  useEffect(() => {
    let cancelled = false;
    if (active.length === 0) {
      setFxToDefault(new Map([[chartCurrency, 1]]));
      setFxChartStatus("ready");
      return;
    }
    const currencies = active.map((s) => (s.currency || "USD").toUpperCase());
    setFxChartStatus("loading");
    void (async () => {
      try {
        const mult = await buildToDefaultMultipliers(currencies, chartCurrency);
        if (!cancelled) {
          setFxToDefault(mult);
          setFxChartStatus("ready");
        }
      } catch {
        if (!cancelled) {
          setFxChartStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, chartCurrency]);

  const totalsByCurrency = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of active) {
      const c = (s.currency || "USD").toUpperCase();
      map.set(c, (map.get(c) ?? 0) + monthlyEquivalent(s));
    }
    return map;
  }, [active]);

  const primaryCurrency =
    active[0]?.currency?.toUpperCase() ?? chartCurrency;

  /** Sum of monthly equivalents in the user’s default currency (ECB reference via Frankfurter). */
  const totalMonthlyInDefault = useMemo(() => {
    if (fxChartStatus !== "ready") return null;
    let sum = 0;
    for (const s of active) {
      const m = monthlyEquivalent(s);
      const cur = (s.currency || "USD").toUpperCase();
      const r = fxToDefault.get(cur) ?? 1;
      sum += m * r;
    }
    return Math.round(sum * 100) / 100;
  }, [active, fxChartStatus, fxToDefault]);

  const statsLoading = fxChartStatus === "loading" && active.length > 0;
  const statsFxError = fxChartStatus === "error" && active.length > 0;

  const monthlySummary = (() => {
    if (active.length === 0) {
      return formatMoney(0, chartCurrency, nfLocale);
    }
    if (statsLoading) return "Loading…";
    if (totalMonthlyInDefault !== null) {
      return formatMoney(totalMonthlyInDefault, chartCurrency, nfLocale);
    }
    if (totalsByCurrency.size <= 1) {
      return formatMoney(
        [...totalsByCurrency.values()][0] ?? 0,
        primaryCurrency,
        nfLocale,
      );
    }
    return [...totalsByCurrency.entries()]
      .map(([c, v]) => formatMoney(v, c, nfLocale))
      .join(" · ");
  })();

  const yearlySummary = (() => {
    if (active.length === 0) {
      return formatMoney(0, chartCurrency, nfLocale);
    }
    if (statsLoading) return "Loading…";
    if (totalMonthlyInDefault !== null) {
      return formatMoney(totalMonthlyInDefault * 12, chartCurrency, nfLocale);
    }
    if (totalsByCurrency.size <= 1) {
      return formatMoney(
        ([...totalsByCurrency.values()][0] ?? 0) * 12,
        primaryCurrency,
        nfLocale,
      );
    }
    return [...totalsByCurrency.entries()]
      .map(([c, v]) => formatMoney(v * 12, c, nfLocale))
      .join(" · ");
  })();

  const monthlyStatHint =
    totalMonthlyInDefault !== null && active.length > 0
      ? `Active plans, monthly equivalent in ${chartCurrency} (live ECB reference rates).`
      : statsFxError && totalsByCurrency.size > 1
        ? "Could not load rates; amounts shown in original currencies."
        : "Active subscriptions, normalized to monthly";

  const yearlyStatHint =
    totalMonthlyInDefault !== null && active.length > 0
      ? `Monthly × 12 in ${chartCurrency} (same rates as overview).`
      : statsFxError && totalsByCurrency.size > 1
        ? "Could not load rates; amounts shown in original currencies."
        : "Monthly × 12 at today’s mix";

  /** One row per active subscription — ranked bar view (raw monthly eq., per-row currency). */
  const subscriptionRowsRaw = useMemo((): SubscriptionChartRow[] => {
    const rows = active.map((s) => {
      const value = Math.round(monthlyEquivalent(s) * 100) / 100;
      const name = s.name?.trim() || "Unnamed";
      const short =
        name.length > 22 ? `${name.slice(0, 20).trimEnd()}…` : name;
      return {
        id: s.id,
        name,
        short,
        value,
        currency: (s.currency || "USD").toUpperCase(),
        billing_cycle: s.billing_cycle,
        next_renewal_date: s.next_renewal_date,
      };
    });
    rows.sort((a, b) => b.value - a.value);
    return rows;
  }, [active]);

  const subscriptionSpendTotalRaw = useMemo(
    () => subscriptionRowsRaw.reduce((sum, r) => sum + r.value, 0),
    [subscriptionRowsRaw],
  );

  const chartNormalized = useMemo(() => {
    if (fxChartStatus !== "ready") return null;
    const mult = fxToDefault;
    const toDefault = (amount: number, from: string) => {
      const f = from.toUpperCase();
      const r = mult.get(f) ?? 1;
      return Math.round(amount * r * 100) / 100;
    };

    const subscriptionRows: SubscriptionChartRow[] = active.map((s) => {
      const monthly = monthlyEquivalent(s);
      const cur = (s.currency || "USD").toUpperCase();
      const value = toDefault(monthly, cur);
      const name = s.name?.trim() || "Unnamed";
      const short =
        name.length > 22 ? `${name.slice(0, 20).trimEnd()}…` : name;
      return {
        id: s.id,
        name,
        short,
        value,
        currency: chartCurrency,
        origMonthly: monthly,
        origCurrency: cur,
        billing_cycle: s.billing_cycle,
        next_renewal_date: s.next_renewal_date,
      };
    });
    subscriptionRows.sort((a, b) => b.value - a.value);
    const subscriptionSpendTotal = subscriptionRows.reduce(
      (sum, r) => sum + r.value,
      0,
    );
    return { subscriptionRows, subscriptionSpendTotal };
  }, [active, fxChartStatus, fxToDefault, chartCurrency]);

  const subscriptionChartRows =
    chartNormalized?.subscriptionRows ?? subscriptionRowsRaw;
  const subscriptionSpendTotal =
    chartNormalized?.subscriptionSpendTotal ?? subscriptionSpendTotalRaw;
  const chartLoading = fxChartStatus === "loading" && active.length > 0;
  const chartMixedCurrencyFallback =
    fxChartStatus === "error" && totalsByCurrency.size > 1;

  const upcoming = useMemo(() => {
    return active
      .map((s) => ({ sub: s, days: daysUntil(s.next_renewal_date) }))
      .filter(
        ({ days }) => days >= 0 && days <= renewalWindow,
      )
      .sort((a, b) => a.days - b.days);
  }, [active, renewalWindow]);

  return (
    <div className="space-y-8">
      <section className="grid gap-4 sm:grid-cols-3">
        <StatCard
          icon={<PiggyBank className="h-5 w-5 text-accent" />}
          label="Monthly equivalent"
          value={monthlySummary}
          hint={monthlyStatHint}
        />
        <StatCard
          icon={<TrendingUp className="h-5 w-5 text-accent-muted" />}
          label="Yearly projection"
          value={yearlySummary}
          hint={yearlyStatHint}
        />
        <StatCard
          icon={<CalendarClock className="h-5 w-5 text-accent" />}
          label="Active plans"
          value={String(active.length)}
          hint="Included in totals"
        />
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-surface-1/80 p-5 backdrop-blur-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="font-display text-base font-semibold text-text">
                Cost by subscription
              </h2>
              <p className="mt-1 text-sm text-text-muted">
                Each active plan ranked by monthly equivalent — amounts and
                renewal in tooltips
                {chartMixedCurrencyFallback && (
                  <span className="mt-1 block text-amber-soft">
                    Could not load exchange rates; chart mixes raw amounts by currency.
                  </span>
                )}
              </p>
            </div>
          </div>
          {chartLoading ? (
            <p className="mt-8 text-center text-sm text-text-muted">
              Loading exchange rates to chart in {chartCurrency}…
            </p>
          ) : subscriptionChartRows.length === 0 ? (
            <p className="mt-8 text-center text-sm text-text-muted">
              Add active subscriptions to see the breakdown.
            </p>
          ) : (
            <div
              className={cn(
                "mt-4 w-full min-h-[240px] select-none",
                subscriptionChartRows.length > 6 ? "h-[340px]" : "h-[280px]",
              )}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={subscriptionChartRows}
                  margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
                  barCategoryGap={6}
                >
                  <XAxis
                    type="number"
                    hide
                    domain={[0, "dataMax"]}
                  />
                  <YAxis
                    type="category"
                    dataKey="short"
                    width={118}
                    tick={{
                      fill: "var(--color-text-muted)",
                      fontSize: 11,
                    }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: "var(--color-surface-2)", opacity: 0.35 }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const row = payload[0].payload as SubscriptionChartRow;
                      const pct =
                        subscriptionSpendTotal > 0
                          ? Math.round(
                              (row.value / subscriptionSpendTotal) * 1000,
                            ) / 10
                          : 0;
                      const days = daysUntil(row.next_renewal_date);
                      const renew =
                        days < 0
                          ? "renewal past"
                          : days === 0
                            ? "renews today"
                            : `renews in ${days}d`;
                      const showOrig =
                        row.origCurrency != null &&
                        row.origMonthly != null &&
                        row.origCurrency !== chartCurrency;
                      return (
                        <div
                          className="rounded-xl border px-3 py-2.5 text-sm shadow-lg"
                          style={{
                            background: "var(--color-tooltip-bg)",
                            borderColor: "var(--color-tooltip-border)",
                            color: "var(--color-tooltip-fg)",
                          }}
                        >
                          <p className="font-medium">{row.name}</p>
                          <p className="mt-1 text-[0.8125rem] opacity-95">
                            {formatMoney(row.value, row.currency, nfLocale)}{" "}
                            / mo eq. · {pct}% of tracked monthly total
                          </p>
                          {showOrig && (
                            <p className="mt-0.5 text-xs opacity-80">
                              Billed at{" "}
                              {formatMoney(
                                row.origMonthly!,
                                row.origCurrency!,
                                nfLocale,
                              )}{" "}
                              / mo eq.
                            </p>
                          )}
                          <p className="mt-0.5 text-xs opacity-80 capitalize">
                            {row.billing_cycle.replace(/_/g, " ")} · {renew}
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={22}>
                    {subscriptionChartRows.map((_, i) => (
                      <Cell
                        key={subscriptionChartRows[i].id}
                        fill={CHART_COLORS[i % CHART_COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-surface-1/80 p-5 backdrop-blur-sm">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-display text-base font-semibold text-text">
                Upcoming renewals
              </h2>
              <p className="mt-1 text-sm text-text-muted">
                Active plans renewing within the window
              </p>
            </div>
            <div className="flex rounded-lg border border-border bg-surface-0 p-0.5">
              {([30, 60, 90] as const).map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setRenewalWindow(w)}
                  className={cn(
                    "touch-manipulation rounded-md px-3 py-2 text-xs font-medium transition-colors min-[480px]:py-1.5",
                    renewalWindow === w
                      ? "bg-surface-2 text-text"
                      : "text-text-muted hover:text-text",
                  )}
                >
                  {w}d
                </button>
              ))}
            </div>
          </div>
          <ul className="mt-4 max-h-[280px] space-y-2 overflow-y-auto pr-1">
            {upcoming.length === 0 ? (
              <li className="rounded-xl border border-dashed border-border py-8 text-center text-sm text-text-muted">
                No renewals in the next {renewalWindow} days.
              </li>
            ) : (
              upcoming.map(({ sub, days }) => (
                <li
                  key={sub.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border/80 bg-surface-0/60 px-3 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-text">{sub.name}</p>
                    <p className="text-xs text-text-muted">{sub.category}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold text-accent">
                      {days === 0 ? "Today" : `${days}d`}
                    </p>
                    <p className="text-xs text-text-muted">
                      {statsLoading ? (
                        "…"
                      ) : (
                        <>
                          {formatMoney(
                            totalMonthlyInDefault !== null
                              ? Math.round(
                                  monthlyEquivalent(sub) *
                                    (fxToDefault.get(
                                      (sub.currency || "USD").toUpperCase(),
                                    ) ?? 1) *
                                    100,
                                ) / 100
                              : monthlyEquivalent(sub),
                            totalMonthlyInDefault !== null
                              ? chartCurrency
                              : (sub.currency || "USD").toUpperCase(),
                            nfLocale,
                          )}
                          /mo eq.
                          {totalMonthlyInDefault !== null &&
                            (sub.currency || "USD").toUpperCase() !==
                              chartCurrency && (
                              <span className="mt-0.5 block opacity-80">
                                Billed at{" "}
                                {formatMoney(
                                  monthlyEquivalent(sub),
                                  (sub.currency || "USD").toUpperCase(),
                                  nfLocale,
                                )}{" "}
                                / mo eq.
                              </span>
                            )}
                        </>
                      )}
                    </p>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </section>
    </div>
  );
}

function StatCard(props: {
  icon: ReactNode;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-surface-1/90 p-4 backdrop-blur-sm">
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-surface-2">
          {props.icon}
        </div>
        <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
          {props.label}
        </p>
      </div>
      <p className="mt-3 font-display text-2xl font-semibold tracking-tight text-text">
        {props.value}
      </p>
      <p className="mt-1 text-xs text-text-muted">{props.hint}</p>
    </div>
  );
}
